import { Badge, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatSampleDateTime } from '../../utils/dateFnsLocale';
import { formatUserName } from '../../utils/name.util';
import {
  formatUsageRecordActionLabel,
  usageActionBadgeBg,
} from '../../utils/sampleUsageActionDisplay';
import { type EnrichedUsageRecord } from '../../utils/sampleUsageRecords';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';

export type SampleDetailUsageRecentListProps = {
  recentRecords: EnrichedUsageRecord[];
  totalCount: number;
  onViewAllHistory: () => void;
  /** サンプル単位（空なら表示既定 vials） */
  unit?: string | null;
};

function recordedByLabel(record: EnrichedUsageRecord, language: string): string {
  const formatted = record.user
    ? formatUserName(record.user.firstName, record.user.lastName, language)
    : '';
  return formatted || record.user?.username || record.recordedBy || '-';
}

function quantityDisplay(
  record: EnrichedUsageRecord,
  unit: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (record.isCreationRecord) return '-';
  return formatSampleQuantityWithUnit(record.quantity, unit, t);
}

function remainingDisplay(
  record: EnrichedUsageRecord,
  unit: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  return formatSampleQuantityWithUnit(record.calculatedRemaining, unit, t);
}

function ActionBadge({ record }: { record: EnrichedUsageRecord }) {
  const { t } = useTranslation();
  const actionText = formatUsageRecordActionLabel(record, t);
  if (record.isCreationRecord) {
    return <Badge bg="success">{actionText}</Badge>;
  }
  return <Badge bg={usageActionBadgeBg(record.action)}>{actionText}</Badge>;
}

/** 詳細の最近使用履歴（モバイルはカード、md+ は表） */
export default function SampleDetailUsageRecentList({
  recentRecords,
  totalCount,
  onViewAllHistory,
  unit,
}: SampleDetailUsageRecentListProps) {
  const { t, i18n } = useTranslation();

  if (recentRecords.length === 0) {
    return (
      <p className="mb-0 small text-muted sample-detail-usage-empty" role="status">
        {joinLabeledValue(
          t('usage_record.no_records'),
          t('usage_record.empty_use_header_hint')
        )}
      </p>
    );
  }

  /* プレビューで全件見えているときは「表示」リンクを出さない（誤誘導） */
  const moreLink =
    totalCount > recentRecords.length ? (
      <div className="text-center mt-2 py-1">
        <Button variant="outline-primary" size="sm" onClick={onViewAllHistory}>
          <i className="fas fa-history me-1" aria-hidden="true" />
          {t('sample.view_usage_history')}{' '}
          ({t('sample.view_usage_history_record_count', { count: totalCount })})
        </Button>
      </div>
    ) : null;

  return (
    <>
      <div className="d-md-none sample-detail-usage-cards">
        {recentRecords.map((record) => (
          <div
            key={record.id}
            className={`sample-detail-usage-card border rounded px-2 py-2 mb-2${
              record.isCreationRecord ? ' border-success-subtle bg-success-subtle' : ''
            }`}
          >
            <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
              <ActionBadge record={record} />
              <time className="small text-muted text-nowrap">
                {record.usageDate
                  ? formatSampleDateTime(record.usageDate, i18n.language)
                  : '-'}
              </time>
            </div>
            <div className="sample-detail-usage-card-metrics small">
              <span className="sample-detail-usage-card-metric">
                {joinLabeledValue(
                  t('usage_record.table_header_quantity'),
                  quantityDisplay(record, unit, t)
                )}
              </span>
              <span className="sample-detail-usage-card-metric" aria-hidden="true">
                {' · '}
              </span>
              <span className="sample-detail-usage-card-metric">
                <strong className="text-primary">
                  {joinLabeledValue(
                    t('usage_record.table_header_remaining'),
                    remainingDisplay(record, unit, t)
                  )}
                </strong>
              </span>
            </div>
            {record.notes ? (
              <div className="small text-muted mt-1 text-break">{record.notes}</div>
            ) : null}
            <div className="small text-muted mt-1">
              {recordedByLabel(record, i18n.language)}
            </div>
          </div>
        ))}
      </div>

      <div className="table-responsive d-none d-md-block">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead className="table-light">
            <tr>
              <th>{t('usage_record.table_header_usage_date')}</th>
              <th>{t('usage_record.table_header_action')}</th>
              <th>{t('usage_record.table_header_detail')}</th>
              <th>{t('usage_record.table_header_quantity')}</th>
              <th>{t('usage_record.table_header_remaining')}</th>
              <th>{t('usage_record.table_header_recorded_by')}</th>
            </tr>
          </thead>
          <tbody>
            {recentRecords.map((record) => (
              <tr
                key={record.id}
                className={record.isCreationRecord ? 'table-success' : undefined}
              >
                <td>
                  {record.usageDate
                    ? formatSampleDateTime(record.usageDate, i18n.language)
                    : '-'}
                </td>
                <td>
                  <ActionBadge record={record} />
                </td>
                <td>{record.notes || '-'}</td>
                <td className="text-end">{quantityDisplay(record, unit, t)}</td>
                <td className="text-end fw-bold text-primary sample-detail-usage-remaining">
                  {remainingDisplay(record, unit, t)}
                </td>
                <td>{recordedByLabel(record, i18n.language)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {moreLink}
    </>
  );
}
